import { supabase } from "@/integrations/supabase/client";

/**
 * Manual "Delivered" fan-out.
 *
 * When a dispatcher sets Status = Delivered on the Edit Order form, the
 * parent `orders` row is the only thing that changes — leg rows keep their
 * stale `driver_status` (often `null` or `in_transit`). Read paths cap the
 * parent's contribution at `confirmed` for multi-leg orders
 * (`src/lib/orders-with-legs.ts`), so those legs render as "confirmed" while
 * the order says "delivered". This helper reconciles the legs so downstream
 * consumers see one consistent status.
 *
 * Scope: local DB reconciliation only. No EAPI calls, no trailer events —
 * those stay owned by the driver / yard / poller flows.
 */

export interface FanoutLegRow {
  id: string;
  leg_sequence: number;
  driver_status: string | null;
  actual_departure_time?: string | null;
  actual_arrival_time?: string | null;
}

export interface LegDeliveryUpdate {
  id: string;
  leg_sequence: number;
  previousStatus: string | null;
  update: {
    driver_status: "delivered";
    actual_arrival_time?: string;
    actual_departure_time?: string;
  };
}

/**
 * Pure: pick the legs that need to be flipped to `delivered` and compute the
 * write-once timestamp stamps for each.
 *
 *  - already `delivered` → skipped (no-op)
 *  - `canceled` → skipped (terminal & orthogonal, stays canceled)
 *  - `actual_arrival_time` stamped only when null
 *  - `actual_departure_time` backfilled only when null (mirrors `progressLeg`)
 */
export function selectLegDeliveryUpdates(
  legs: FanoutLegRow[],
  nowIso: string,
): LegDeliveryUpdate[] {
  const out: LegDeliveryUpdate[] = [];
  for (const leg of legs) {
    if (leg.driver_status === "delivered") continue;
    if (leg.driver_status === "canceled") continue;
    const update: LegDeliveryUpdate["update"] = { driver_status: "delivered" };
    if (!leg.actual_arrival_time) update.actual_arrival_time = nowIso;
    if (!leg.actual_departure_time) update.actual_departure_time = nowIso;
    out.push({
      id: leg.id,
      leg_sequence: leg.leg_sequence,
      previousStatus: leg.driver_status ?? null,
      update,
    });
  }
  return out;
}

export interface FanOutDeliveredInput {
  orderId: string;
  tenantId?: string | null;
  changedBy: string;
}

/**
 * IO: load the order's legs, flip every non-terminal one to `delivered`, and
 * write a `leg_<n>_driver_status` entry to `order_change_log` for each so the
 * order timeline attributes the manual fan-out.
 *
 * Best-effort: failures are logged and swallowed so a reconciliation problem
 * never rolls back an otherwise successful order edit.
 */
export async function fanOutDeliveredToLegs({
  orderId,
  tenantId,
  changedBy,
}: FanOutDeliveredInput): Promise<LegDeliveryUpdate[]> {
  try {
    const { data, error } = await supabase
      .from("legs")
      .select("id, leg_sequence, driver_status, actual_departure_time, actual_arrival_time")
      .eq("order_id", orderId)
      .order("leg_sequence", { ascending: true });
    if (error) throw error;

    const updates = selectLegDeliveryUpdates((data ?? []) as FanoutLegRow[], new Date().toISOString());
    if (!updates.length) return [];

    for (const u of updates) {
      const { error: upErr } = await supabase.from("legs").update(u.update).eq("id", u.id);
      if (upErr) throw upErr;
    }

    await supabase.from("order_change_log").insert(
      updates.map((u) => ({
        order_id: orderId,
        changed_by: changedBy || "Unknown",
        ...(tenantId ? { tenant_id: tenantId } : {}),
        field_name: `leg_${u.leg_sequence}_driver_status`,
        old_value: u.previousStatus,
        new_value: "delivered",
      })),
    );

    return updates;
  } catch (err) {
    console.warn("[delivered fan-out] failed to propagate delivered to legs:", err);
    return [];
  }
}
